import React from 'react';

/**
 * Minimal SVG sparkline — no dependencies. Renders the supplied
 * numeric series as a polyline scaled to fit the viewBox. Uses the
 * AdminDashboard accent palette (teal → purple gradient) so it
 * matches the Codebase Health tile.
 *
 * Props:
 *   values    number[]  numeric series (min 2 entries)
 *   width     number    px width  (default 96)
 *   height    number    px height (default 24)
 *   className string    extra wrapper classes
 *   testid    string    optional data-testid for the wrapper svg
 */
export default function Sparkline({
  values = [],
  width = 96,
  height = 24,
  className = '',
  testid,
}) {
  if (!values || values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);

  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * (height - 2) - 1;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  const lastX = (values.length - 1) * stepX;
  const lastY =
    height - ((values[values.length - 1] - min) / range) * (height - 2) - 1;

  // Direction colour: green if latest <= 95% of median, red if latest
  // > 1.5× median, otherwise teal. Lets the eye catch a regression.
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const last = values[values.length - 1];
  const strokeColor =
    last <= median * 0.95
      ? '#10b981' // green
      : last > median * 1.5
      ? '#ef4444' // red
      : '#00E5CC'; // teal

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      data-testid={testid}
      role="img"
      aria-label={`Trend: ${values.length} samples, min ${min}, max ${max}, latest ${last}`}
    >
      <polyline
        points={points}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastX} cy={lastY} r="2" fill={strokeColor} />
    </svg>
  );
}
